/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import type { NewClassGetYearGroupsItem } from '@whizz/api-model/src/endpoints/NewClass';
import type { AxiosError } from 'axios';
import { useI18n } from '@whizz/react-i18n';
import { Button, Input, Loader } from '../../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../../global';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../../util/vars';
import { repository } from '@whizz/api-client';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { YearGroupSelector } from '../../../modules';
import { ColorPicker } from '../../../modules/ColorPicker';
import { getDefaultTagColor } from '../../../../../util/styled';
import { ModalFooter } from '../../../../../components/modal/Modal';

const MIN_LABEL_WIDTH = 150;

const AddEditClassModal = ({ toggle, toggleConfirm, data, yearGroups, classCount = 1, onSuccess, onError }: ClassModalProps): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const editMode = null != data;

  const studentsCountQuery = useQuery<number>(
    [ QUERIES_IDS.STUDENTS_COUNT, data?.id ],
    () => repository.NewClassRepository.getStudentsCount(data!.id),
    {
      enabled: editMode,
      placeholderData: undefined,
    },
  );

  const mutations = {
    create: useMutation(
      (formData: FormData) => repository.NewClassRepository.create(formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
          toggle(false);
        },
      },
    ),
    edit: useMutation(
      (formData: FormData) => repository.NewClassRepository.update(formData.id!, formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
          toggle(false);
        },
      },
    ),
  };

  const handleDelete = () => {
    toggle(false);
    toggleConfirm(true);
  };

  const hasStudents = null != studentsCountQuery.data && 0 < studentsCountQuery.data;

  const VALIDATION_SCHEMA = Yup.object().shape({
    name: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.VALIDATIONS.NAME.REQUIRED')),
    yearGroupId: Yup.number().nullable(),
    color: Yup.number().nullable(),
  });

  if (editMode && (!studentsCountQuery.isFetched || studentsCountQuery.isFetching)) {
    return (<Loader />);
  }

  return (
    <Formik<FormData>
      validationSchema={VALIDATION_SCHEMA}
      initialValues={{
        name: '',
        yearGroupId: null,
        color: getDefaultTagColor(classCount),
        ...(editMode ? data : {}),
      }}
      onSubmit={(formData) => {
        if (editMode) {
          mutations.edit.mutate(formData);
        } else {
          mutations.create.mutate(formData);
        }
      }}
    >
      {({ handleSubmit, values, getFieldProps, setFieldValue, errors, resetForm, isValid, touched }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container space-y-6'}>
            <Input
              {...getFieldProps('name')}
              id={'name'}
              label={translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.CLASS_NAME')}
              labelStyle={'bold'}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.name ? errors.name : null}
              required
            />
            <YearGroupSelector
              label={translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.YEAR_GROUP')}
              labelStyle={'bold'}
              onChange={(selectedOption) => setFieldValue('yearGroupId', selectedOption.value)}
              value={values.yearGroupId}
              error={touched.yearGroupId ? errors.yearGroupId : null}
              options={yearGroups.map((yearGroup) => ({ label: yearGroup.name, value: yearGroup.id }))}
            />

            <Field type={'hidden'} value={values.color} name={'color'} />
            <ColorPicker
              title={translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.LABEL_COLOR')}
              label={values.name}
              value={values.color}
              onChange={(value) => setFieldValue('color', value)}
            />

            {(!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData]))
            && (<FormGenericError />)}
          </div>
          <ModalFooter justify={editMode ? 'between' : 'end'}>
            {editMode && (
              <CancelButtonModal
                align={'start'}
                onClick={() => resetForm()}
              >
                {translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.BUTTONS.DISCARD')}
              </CancelButtonModal>
            )}
            <SaveButton type={'submit'} />
          </ModalFooter>
          {editMode && (
            <div className={'container space-y-6'}>
              <h5 className={'font-bold'}>
                {translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.DELETE_TITLE')}
              </h5>

              {hasStudents && (
                <>
                  <p>{translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.DELETE_HINT_1')}</p>
                  <p>{translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.DELETE_HINT_2')}</p>
                </>
              )}

              <Button
                color={'negative'}
                icon={GLOBAL_ICONS.delete}
                isOutline
                onClick={handleDelete}
                disabled={hasStudents}
              >
                {translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.BUTTONS.DELETE')}
              </Button>
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};

export default AddEditClassModal;

export interface ClassModalProps extends Pick<ToggleModalProps, 'toggle'> {
  toggleConfirm(isOpen: boolean): void;
  data?: EditMutationProps;
  yearGroups: NewClassGetYearGroupsItem[];
  classCount: number;
  onSuccess?: () => void;
  onError?: () => void;
}

export interface FormData {
  id?: number | null;
  name: string;
  color: number;
  yearGroupId: number | null;
}

export interface CreateMutationProps {
  name: string;
  yearGroupId: number | null;
}

export interface EditMutationProps extends CreateMutationProps {
  id: number;
  color: number;
}
